<svelte:options runes={false} />
<script>
  // Portfolio surface (U15/KTD12): the current user's server-aggregated, cross-strategy view — portfolio
  // NAV/PnL metrics + equity/drawdown charts + a combined blotter (each row tagged with its strategy), plus
  // portfolio-level deposit/withdraw (PortfolioRatio fan-out → async adjust, same Pending→Applied/Failed
  // lifecycle as U14) and a join-discovery list. All scoped to the user's selected-org membership (the U5 gate).
  // Deposit/withdraw + join are owner/admin-gated server-side (U9/U10b); a read-only participant sees the
  // monitoring view but the management controls are disabled with a clear reason.
  import { onMount, onDestroy } from 'svelte';
  import api from '../../lib/api.js';
  import { selectedOrgId, canTrade } from '../../lib/orgContext.js';
  import { contextTarget, targetLabel, targetKey } from '../../lib/runContext.js';
  import {
    portfolioPerformance, portfolioBlotter, portfolioDiscoverable, portfolioLoading, portfolioError,
    adjustments, recordPendingAdjustment,
  } from '../../lib/autotradeStore.js';
  import { isResultMeaningful } from '../../lib/strategyResult.js';
  import { formatCurrency } from '../../lib/store.js';
  import { SUPPORTED_QUOTE_CURRENCIES, formatQuoteAmount } from '../../lib/quoteCurrency.js';
  import { displayCurrency, setDisplayCurrency, usdRates, refreshRates, sumInCurrency } from '../../lib/currencyContext.js';

  import PerformancePanel from '../shared/PerformancePanel.svelte';
  import TradesTable from '../shared/TradesTable.svelte';
  import PositionsTable from '../shared/PositionsTable.svelte';
  import EquityCurveChart from '../shared/charts/EquityCurveChart.svelte';
  import DrawdownChart from '../shared/charts/DrawdownChart.svelte';
  import ReturnsChart from '../shared/charts/ReturnsChart.svelte';
  import PortfolioDiscovery from './PortfolioDiscovery.svelte';
  import OrgMultiSelect from './OrgMultiSelect.svelte';

  // The portfolio is scoped to ONE run target {venue, mode} (ADR-0011/0012 → ADR-0040) — targets are never
  // combined: a Paper and a Live book on the same venue are two portfolios. Normally the header context's
  // target; the Paper Trading view passes (Coinbase, Paper). Every read below passes portfolioTarget (the
  // server REQUIRES `mode`); a target change re-scopes the whole surface (like an org switch).
  export let target = null;
  $: portfolioTarget = target ?? $contextTarget;
  $: portfolioVenue = portfolioTarget.venue; // FX quotes are venue-scoped (never a mode)


  let busy = false;
  let actionError = null;
  let depositAmount = '';
  let pollTimer = null;

  // U8/KTD8: the org's consolidated holdings — Total NAV across strategies + per-symbol net open positions.
  // null = not yet loaded (skeleton); { totalNav, navByCurrency, positions } once loaded.
  let consolidated = null;

  // ADR-0014: the portfolio display currency — always opens in USD (owner decision); the selector converts
  // every displayed amount at spot (BTC/ETH via the quote endpoint; USD/USDC par). Each strategy's NAV is
  // denominated in ITS OWN currency (navByCurrency); the total is the converted sum — '—' until every
  // needed rate is known (never a silently-wrong partial sum).
  $: displayNav = consolidated
    ? sumInCurrency(
        consolidated.navByCurrency ?? (consolidated.totalNav != null ? { USD: consolidated.totalNav } : null),
        $displayCurrency,
        $usdRates)
    : null;
  function pickCurrency(c) {
    setDisplayCurrency(c);
    refreshRates((symbol) => api.getQuote(symbol, portfolioVenue)); // spot refresh on switch
  }

  // U5: the header org multi-select. `orgOptions` = [{orgId, orgName}] the caller may select; `selectedOrgIds`
  // = the chosen subset (defaults to all, >=1 enforced); `orgState` = the participating-orgs fetch state.
  let orgOptions = [];
  let selectedOrgIds = [];
  let orgState = 'loading';
  // A monotonically-increasing fetch token: a load started for an older (strategy/mode/org-set) context is
  // discarded when it resolves after a newer one — the set-aware race guard (the scalar selectedOrgId guard
  // alone is not sufficient for a set).
  let loadToken = 0;

  $: result = $portfolioPerformance;
  $: meaningful = isResultMeaningful(result);

  // Titled by the TARGET's salience label — 'unknown' can only arise from a malformed prop and is never 'Live'.
  const PORTFOLIO_TITLES = Object.freeze({
    live: 'Live Portfolio', paper: 'Paper Portfolio', sandbox: 'Sandbox Portfolio', unknown: 'Portfolio',
  });

  // The combined blotter as trade records for the shared TradesTable (each row tagged with its strategy).
  $: blotterTrades = ($portfolioBlotter ?? []).map((b) => ({
    symbol: b.symbol, side: b.side, tradeTime: b.time, price: b.price, qty: b.qty, fees: b.fees,
  }));

  // The selected set drives every consolidated read; reload whenever it changes (and on mode change below).
  $: if (orgState === 'ready' && selectedOrgIds.length > 0) { loadPortfolio(); }

  async function loadParticipatingOrgs() {
    orgState = 'loading';
    try {
      const orgs = await api.getParticipatingOrgs(portfolioTarget);
      orgOptions = (orgs ?? []).map((o) => (typeof o === 'string' ? { orgId: o, orgName: o } : o));
      // Default: ALL participating orgs selected.
      selectedOrgIds = orgOptions.map((o) => o.orgId);
      orgState = 'ready';
    } catch (err) {
      orgState = 'error';
    }
  }

  async function loadPortfolio() {
    // TASK-20260818: before the org context resolves, the fallback was `[null]` — four scope-less
    // tenant reads, each a guaranteed 400 (ADR-0023). Skip; the org-change reaction reloads.
    const orgs = selectedOrgIds.length > 0 ? selectedOrgIds : ($selectedOrgId ? [$selectedOrgId] : []);
    if (orgs.length === 0) return;
    const token = ++loadToken;
    portfolioLoading.set(true);
    portfolioError.set(null);
    // Each read settles INDEPENDENTLY (allSettled, not all): one failing endpoint must not blank the others —
    // previously a single rejection (e.g. blotter) threw the whole Promise.all, so Total NAV and Open Positions
    // never rendered even though /positions had succeeded.
    const [perf, blotter, discoverable, positions] = await Promise.allSettled([
      api.getPortfolioPerformance(orgs, portfolioTarget),
      api.getPortfolioBlotter(orgs, portfolioTarget),
      api.getDiscoverable(orgs, portfolioTarget),
      api.getPortfolioPositions(orgs, portfolioTarget),
    ]);
    if (token !== loadToken) return; // set-aware race guard: a newer load superseded this one
    if (perf.status === 'fulfilled') portfolioPerformance.set(perf.value);
    if (blotter.status === 'fulfilled') portfolioBlotter.set(blotter.value ?? []);
    if (discoverable.status === 'fulfilled') portfolioDiscoverable.set(discoverable.value ?? []);
    if (positions.status === 'fulfilled') consolidated = positions.value ?? null;
    // ADR-0014: a portfolio holding non-par-currency strategies (BTC/ETH buckets) needs spot rates for the
    // converted total — fetch once per load; the all-USD/USDC legacy portfolio needs none.
    const buckets = Object.keys(consolidated?.navByCurrency ?? {});
    if (buckets.some((c) => c !== 'USD' && c !== 'USDC') || $displayCurrency !== 'USD') {
      refreshRates((symbol) => api.getQuote(symbol, portfolioVenue));
    }
    const failed = [
      ['performance', perf], ['blotter', blotter], ['discovery', discoverable], ['positions', positions],
    ].filter(([, r]) => r.status === 'rejected');
    if (failed.length) {
      portfolioError.set(failed.map(([name, r]) => `${name}: ${mapError(r.reason)}`).join(' · '));
    }
    portfolioLoading.set(false);
  }

  // ---- Portfolio deposit/withdraw: fans out one Pending adjustment per administrable strategy (U9). The 202
  //      returns { adjustmentIds }; we record each Pending so the user sees the fan-out lifecycle. ----
  async function submitCash(sign) {
    const amount = Number(depositAmount) * sign;
    if (!(Math.abs(amount) > 0)) { actionError = 'Enter a positive amount.'; return; }
    if (busy) return;
    busy = true;
    actionError = null;
    try {
      const out = await api.portfolioCash(amount, $selectedOrgId);
      const ids = out?.adjustmentIds ?? [];
      for (const id of ids) {
        recordPendingAdjustment(id, { orgId: $selectedOrgId, kind: amount > 0 ? 'AddCash' : 'Withdraw', amount });
      }
      if (ids.length === 0) {
        actionError = 'No administrable strategies to fund — you must own a strategy with a portfolio ratio set.';
      }
      depositAmount = '';
      schedulePoll();
    } catch (err) {
      actionError = mapError(err);
    } finally {
      busy = false;
    }
  }

  async function handleJoin(e) {
    const { strategyId, startingCash } = e.detail;
    if (busy) return;
    busy = true;
    actionError = null;
    try {
      const out = await api.joinStrategy(strategyId, startingCash, $selectedOrgId);
      if (out?.adjustmentId) {
        recordPendingAdjustment(out.adjustmentId, { requestId: strategyId, orgId: $selectedOrgId, kind: 'AddOrg', amount: startingCash });
      }
      schedulePoll();
    } catch (err) {
      actionError = mapError(err);
    } finally {
      busy = false;
    }
  }

  function schedulePoll() {
    clearTimeout(pollTimer);
    let attempts = 0;
    const tick = async () => { attempts += 1; await loadPortfolio(); if (attempts < 3) pollTimer = setTimeout(tick, 3000); };
    pollTimer = setTimeout(tick, 3000);
  }

  function mapError(err) {
    const msg = String(err?.message ?? err);
    const idx = msg.indexOf('details: ');
    return idx >= 0 ? msg.slice(idx + 'details: '.length) : msg;
  }

  // Reload the participating-orgs list (and re-default the selection to all) on active-org switch OR target
  // change (M13: the stale guard keys on (venue, mode)) — both change which orgs participate. loadPortfolio()
  // then fires reactively once the new set is in place.
  let lastOrg = $selectedOrgId;
  // Seed from the store expression directly — the $:-declared portfolioTarget is undefined during
  // instance-script init, and an undefined seed double-fires loadParticipatingOrgs on every mount.
  let lastTargetKey = targetKey(target ?? $contextTarget);
  $: if ($selectedOrgId !== lastOrg || targetKey(portfolioTarget) !== lastTargetKey) {
    lastOrg = $selectedOrgId; lastTargetKey = targetKey(portfolioTarget); loadParticipatingOrgs();
  }

  // The recent portfolio-related adjustments (the fan-out lifecycle strip).
  $: recentAdjustments = [...$adjustments.values()].sort((a, b) => (b.at ?? 0) - (a.at ?? 0)).slice(0, 10);

  onMount(loadParticipatingOrgs);
  onDestroy(() => clearTimeout(pollTimer));
</script>

<div class="at-portfolio">
  <div class="at-portfolio-header">
    <div class="at-portfolio-identity">
      <h3 class="at-portfolio-title">{PORTFOLIO_TITLES[targetLabel(portfolioTarget)]}</h3>
      <OrgMultiSelect bind:selected={selectedOrgIds} available={orgOptions} state={orgState} onRetry={loadParticipatingOrgs} />
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadPortfolio} disabled={$portfolioLoading}>Refresh</button>
    </div>
    <!-- Top-right Total NAV (sum of every selected org's strategies' NAV, each converted from its own quote
         currency at spot — ADR-0014), with the display-currency switcher. Opens in USD (owner decision). -->
    <div class="at-portfolio-nav" title="Total NAV — every strategy's NAV converted to the selected display currency at spot">
      <span class="at-portfolio-nav-label">Total NAV</span>
      <span class="at-portfolio-nav-value">{displayNav != null ? formatQuoteAmount(displayNav, $displayCurrency) : '—'}</span>
      <div class="at-currency-toggle" role="group" aria-label="Display currency">
        {#each SUPPORTED_QUOTE_CURRENCIES as c}
          <button type="button" class="at-currency-option" class:active={$displayCurrency === c}
            aria-pressed={$displayCurrency === c} on:click={() => pickCurrency(c)}>
            {c}
          </button>
        {/each}
      </div>
      {#if orgOptions.length > 1 && selectedOrgIds.length < orgOptions.length}
        <span class="at-portfolio-subset-note" title="Showing a subset of orgs">{selectedOrgIds.length} of {orgOptions.length} orgs</span>
      {/if}
    </div>
  </div>

  {#if $portfolioError}
    <div class="alert alert-danger" role="alert">{$portfolioError}
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadPortfolio}>Retry</button>
    </div>
  {/if}

  {#if $portfolioLoading && !result}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if result}
    <PerformancePanel
      {result}
      showUnfilledBanner={false}
      showTradesTable={false}
      noDataTitle="No portfolio activity yet."
      noDataBody="You're not participating in any strategies with NAV history yet. Join a running strategy below to start building your portfolio." />

    {#if meaningful}
      <div class="backtest-charts">
        <EquityCurveChart data={result.equityCurve} trades={result.trades} />
        <DrawdownChart data={result.equityCurve} />
        <ReturnsChart data={result.equityCurve} />
      </div>
    {/if}
  {:else}
    <div class="empty-state">
      <p class="empty-state-description">No portfolio performance data yet. Join a running strategy to get started.</p>
    </div>
  {/if}

  <!-- Consolidated open positions across the org's strategies (U8/KTD8) — net per symbol, from the ledger.
       Deliberately OUTSIDE the performance if-block: positions come from /positions and must render even when
       the performance read has no data or failed (previously they were hidden whenever `result` was falsy). -->
  <section class="at-portfolio-positions">
    <PositionsTable positions={consolidated ? consolidated.positions : null} loading={$portfolioLoading && !consolidated} />
  </section>

  <!-- Portfolio deposit/withdraw (owner/admin-gated; PortfolioRatio fan-out). -->
  <section class="at-portfolio-cash">
      <h4>Portfolio deposit / withdraw</h4>
      {#if !$canTrade}
        <p class="form-hint">Your role is read-only — deposit and withdraw are disabled.</p>
      {:else}
        <div class="at-portfolio-cash-row">
          <input type="number" min="0" step="1000" class="form-input" placeholder="amount"
            bind:value={depositAmount} aria-label="Portfolio amount" disabled={busy} />
          <button type="button" class="btn btn-primary" disabled={busy} on:click={() => submitCash(1)}>Deposit</button>
          <button type="button" class="btn btn-ghost" disabled={busy} on:click={() => submitCash(-1)}>Withdraw</button>
        </div>
        <p class="form-hint">Fans out proportionally across the strategies you own that have a portfolio ratio set. A live strategy's slice re-checks the account balance.</p>
      {/if}
      {#if actionError}<div class="alert alert-danger" role="alert">{actionError}</div>{/if}
    </section>

    <!-- Combined blotter. -->
    <section class="at-portfolio-blotter">
      <h4>Combined trade blotter</h4>
      {#if blotterTrades.length}
        <TradesTable trades={blotterTrades} />
      {:else}
        <p class="empty-state-description">No fills across your participating strategies yet.</p>
      {/if}
    </section>

    <!-- Pending fan-out / join lifecycle. -->
    {#if recentAdjustments.length}
      <section class="at-portfolio-pending">
        <h4>Recent adjustments</h4>
        <ul class="at-pending-strip">
          {#each recentAdjustments as a (a.id)}
            <li class="at-pending-item at-pending-{String(a.status).toLowerCase()}">
              <span class="at-pending-kind">{a.kind}</span>
              {#if a.amount != null}<span class="at-pending-amount">{formatCurrency(a.amount)}</span>{/if}
              <span class="at-pending-status">{a.status}{a.failureReason ? ` — ${a.failureReason}` : ''}</span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <!-- Join-discovery. -->
    <section class="at-portfolio-discovery">
      <PortfolioDiscovery strategies={$portfolioDiscoverable} {busy} on:join={handleJoin} />
    </section>
</div>

<style>
  @import '../Backtesting/Backtesting.css';
  .at-portfolio { display: flex; flex-direction: column; gap: 1rem; }
  .at-portfolio-header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
  .at-portfolio-identity { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
  .at-portfolio-title { margin: 0; font-size: 1.1rem; font-weight: 700; color: var(--text-primary, #e8e9ea); }
  /* Total NAV (U8) — top-right stacked block, matching the Strategy view's header NAV (label over value). */
  .at-portfolio-nav { display: flex; flex-direction: column; align-items: flex-end; }
  .at-portfolio-nav-label { font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted, #8a8b8d); }
  .at-portfolio-nav-value { font-size: 1.15rem; font-weight: 700; color: var(--text-primary, #e8e9ea); font-variant-numeric: tabular-nums; }
  /* ADR-0014: the display-currency switcher (venue-toggle pattern, compact). */
  .at-currency-toggle { display: inline-flex; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 5px; overflow: hidden; }
  .at-currency-option {
    appearance: none; background: transparent; border: none; cursor: pointer;
    padding: 0.15rem 0.5rem; font-size: 0.7rem; font-weight: 600; color: var(--text-secondary, #b5b6b8);
  }
  .at-currency-option + .at-currency-option { border-left: 1px solid var(--border-subtle, #1e1f22); }
  .at-currency-option.active { background: var(--accent, #2563eb); color: #fff; }
  .at-portfolio-subset-note { font-size: 0.65rem; color: var(--warning, #d8a000); }
  .at-portfolio-positions :global(.bt-trades-title) { font-size: 0.9rem; }
  .at-portfolio-cash h4, .at-portfolio-blotter h4, .at-portfolio-pending h4 { margin: 0 0 0.5rem; font-size: 0.9rem; color: var(--text-secondary, #b5b6b8); }
  .at-portfolio-cash-row { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
  .at-pending-strip { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.3rem; }
  .at-pending-item { display: flex; gap: 0.75rem; align-items: center; padding: 0.35rem 0.6rem; border-radius: 5px; font-size: 0.78rem; background: var(--surface-1, rgba(255, 255, 255, 0.03)); }
  .at-pending-pending { border-left: 3px solid var(--warning, #d8a000); }
  .at-pending-applied { border-left: 3px solid var(--success, #1aa260); }
  .at-pending-failed { border-left: 3px solid var(--danger, #e0533a); }
  .at-pending-kind { font-weight: 600; }
  .at-pending-amount { font-variant-numeric: tabular-nums; }
  .at-pending-status { margin-left: auto; color: var(--text-muted, #8a8b8d); font-variant-numeric: tabular-nums; }
</style>
